import React, { useCallback, useEffect, useRef, useState } from "react";
import { TableDropDown } from "@solsys/solsys-reactjs";
import { gql, useQuery } from "@apollo/client";

const gqlQuery = gql`
  query ticket_typeDropDown($ticket_type_list_request: ticket_type_list_request) {
    ticket_type(ticket_type_list_request: $ticket_type_list_request) {
      edges {
        id
        name
      }
      totalCount
    }
  }
`;

const mapEdges = (edges) =>
  (edges ?? []).map((item) => ({
    ...item,
    ID: item.id,
    Name: item.name,
  }));

const TicketTypeDropDown = ({ selected, ...props }) => {
  selected = selected === -1 ? undefined : selected;

  const truncateLength = 25;

  const [requestState, setRequestState] = useState({
    page: 1,
    pageSize: 20,
    search: "",
  });

  const { page, pageSize, search } = requestState;

  const [accumulatedList, setAccumulatedList] = useState([]);
  const filtersKey = JSON.stringify([
    { field: "active", operator: "=", value: "1" },
    ...(props.filters ?? []),
  ]);
  const sortingKey = JSON.stringify(
    props.sorting?.length > 0
      ? props.sorting
      : [{ direction: "ASC", field: "menuorder" }]
  );

  const variables = {
    ticket_type_list_request: {
      pagination: {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
      filters: [{ field: "active", operator: "=", value: "1" }],
      sorting: [{ direction: "ASC", field: "menuorder" }],
    },
  };

  const renderVariables = {
    ticket_type_list_request: {
      pagination: {
        limit: truncateLength,
        offset: 0,
      },
      filters: [{ field: "active", operator: "=", value: "1" }],
      sorting: [{ direction: "ASC", field: "menuorder" }],
    },
  };

  if (
    (Array.isArray(selected) && selected.length > 0) ||
    Number.isInteger(selected)
  ) {
    const array = props.multiple ? [...selected] : [selected];
    renderVariables.ticket_type_list_request.filters.push({
      field: "id",
      operator: "in",
      value: `(${array.join(", ")})`,
    });
  }

  if (search && search !== "") {
    variables.ticket_type_list_request.filters.push({
      field: "name",
      operator: "like",
      value: `%${search}%`,
    });
  }

  if (props.filters?.length > 0) {
    props.filters.forEach((f) =>
      variables.ticket_type_list_request.filters.push(f)
    );
  }

  variables.ticket_type_list_request.sorting =
    props.sorting?.length > 0
      ? props.sorting
      : [{ direction: "ASC", field: "menuorder" }];

  const { refetch: renderRefetch, ...renderObject } = useQuery(gqlQuery, {
    variables: renderVariables,
  });
  const renderLoading = renderObject.loading;
  const renderError = renderObject.error;
  const renderData = renderObject.data;

  useEffect(() => {
    renderRefetch();
  }, [selected, renderRefetch]);

  const renderEntityListResponse = renderData?.ticket_type;
  const renderEntityRaw = renderEntityListResponse?.edges ?? [];
  const renderEntity = renderEntityRaw.map((item) => ({
    ...item,
    ID: item.id,
    Name: item.name,
  }));

  const { refetch: refetchTicketType, loading, error, data } = useQuery(
    gqlQuery,
    { variables }
  );

  useEffect(() => {
    if (!props.disabled) {
      refetchTicketType();
    }
  }, [page, search, filtersKey, sortingKey, refetchTicketType, props.disabled]);

  useEffect(() => {
    setAccumulatedList([]);
    setRequestState((s) => ({ ...s, page: 1 }));
  }, [search, filtersKey, sortingKey]);

  useEffect(() => {
    if (loading) return;
    const edges = data?.ticket_type?.edges;
    if (!edges) return;
    const mapped = mapEdges(edges);
    setAccumulatedList((prev) => {
      if (page === 1) return mapped;
      const seen = new Set(prev.map((x) => Number(x.id)));
      const toAdd = mapped.filter((x) => !seen.has(Number(x.id)));
      return [...prev, ...toAdd];
    });
  }, [data, page, loading]);

  const totalCountRef = useRef(0);
  totalCountRef.current = data?.ticket_type?.totalCount ?? 0;
  const accumulatedLenRef = useRef(0);
  accumulatedLenRef.current = accumulatedList.length;

  const onSearch = (event) => {
    const { value } = event.target;
    setRequestState((s) => ({
      ...s,
      search: value,
      page: 1,
    }));
  };

  const paginationChange = (newPage) => {
    setRequestState((s) => ({
      ...s,
      page: newPage,
    }));
  };

  const addMoreResultsHandler = useCallback(() => {
    if (props.disabled) return;
    setRequestState((s) => {
      if (accumulatedLenRef.current >= totalCountRef.current) return s;
      return { ...s, page: s.page + 1 };
    });
  }, [props.disabled]);

  const entityCount = data?.ticket_type?.totalCount ?? 0;

  if (error) {
    console.error(`Ticket type drop down data error : ${error.message}`);
  }
  if (renderError) {
    console.error(
      `Ticket type drop down render error : ${renderError.message}`
    );
  }

  const headers = [{ label: "ID" }, { label: "Name" }];

  return (
    <TableDropDown
      {...props}
      items={accumulatedList}
      loading={loading}
      renderLoading={renderLoading}
      multiple={props.multiple}
      searchEnabled={props.searchEnabled}
      searchValue={search}
      onSearch={onSearch}
      selectValue={selected}
      selectRender={renderEntity}
      onSelect={props.onChange || props.onSelect}
      dataKey={"id"}
      identityColumnKey={"ID"}
      renderColumnKey={"Name"}
      headers={headers}
      truncateLength={truncateLength}
      entityCount={entityCount}
      onPaginationChange={paginationChange}
      addMoreResults={
        typeof props.addMoreResults === "function"
          ? props.addMoreResults
          : addMoreResultsHandler
      }
    />
  );
};

export default TicketTypeDropDown;
